import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import Sidebar from '../../components/layout/Sidebar';
import Header from '../../components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Image,
  Video,
  FileText,
  Palette,
  Upload,
  Plus,
  Download,
  Trash2,
  Eye,
  FolderOpen,
  Search,
  Grid,
  List,
  MoreHorizontal,
  Loader2,
  X,
  ZoomIn,
  ZoomOut,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../components/ui/dropdown-menu';
import api from '../../lib/axios';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';

// Image Viewer Modal Component
const ImageViewer = ({ isOpen, onClose, imageUrl, imageName, allImages, currentIndex, onNavigate }) => {
  const [zoom, setZoom] = useState(1);
  
  if (!isOpen) return null;
  
  const handleZoomIn = () => setZoom(prev => Math.min(prev + 0.25, 3));
  const handleZoomOut = () => setZoom(prev => Math.max(prev - 0.25, 0.5));
  
  const canNavigate = allImages && allImages.length > 1;
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/90" onClick={onClose}>
      <div className="absolute top-4 left-4 right-4 flex items-center justify-between z-10">
        <h3 className="text-white font-medium truncate max-w-[60%]">{imageName}</h3>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            className="text-white hover:bg-white/20 rounded-full"
            onClick={(e) => { e.stopPropagation(); handleZoomOut(); }}
          >
            <ZoomOut className="w-5 h-5" />
          </Button>
          <span className="text-white text-sm min-w-[60px] text-center">{Math.round(zoom * 100)}%</span>
          <Button
            variant="ghost"
            size="icon"
            className="text-white hover:bg-white/20 rounded-full"
            onClick={(e) => { e.stopPropagation(); handleZoomIn(); }}
          >
            <ZoomIn className="w-5 h-5" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="text-white hover:bg-white/20 rounded-full ml-4"
            onClick={onClose}
          >
            <X className="w-6 h-6" />
          </Button>
        </div>
      </div>
      
      {/* Navigation arrows */}
      {canNavigate && (
        <>
          <Button
            variant="ghost"
            size="icon"
            className="absolute left-4 top-1/2 -translate-y-1/2 text-white hover:bg-white/20 rounded-full w-12 h-12"
            onClick={(e) => { e.stopPropagation(); onNavigate('prev'); }}
          >
            <ChevronLeft className="w-8 h-8" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="absolute right-4 top-1/2 -translate-y-1/2 text-white hover:bg-white/20 rounded-full w-12 h-12"
            onClick={(e) => { e.stopPropagation(); onNavigate('next'); }}
          >
            <ChevronRight className="w-8 h-8" />
          </Button>
        </>
      )}
      
      {/* Image */}
      <div 
        className="max-w-[90vw] max-h-[85vh] overflow-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={imageUrl}
          alt={imageName}
          className="transition-transform duration-200"
          style={{ transform: `scale(${zoom})`, transformOrigin: 'center' }}
        />
      </div>
      
      {/* Image counter */}
      {canNavigate && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white text-sm bg-black/50 px-3 py-1 rounded-full">
          {currentIndex + 1} / {allImages.length}
        </div>
      )}
    </div>
  );
};

const SupportsPage = () => {
  const { user, isAgency, isClient } = useAuth();
  const { theme } = useTheme();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [viewMode, setViewMode] = useState('grid');
  const [searchQuery, setSearchQuery] = useState('');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [loading, setLoading] = useState(true);
  
  const [supports, setSupports] = useState([]);
  
  // Image viewer state
  const [imageViewerOpen, setImageViewerOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  const [newSupport, setNewSupport] = useState({
    name: '',
    category: 'logos',
    description: '',
    file: null
  });
  
  const [uploading, setUploading] = useState(false);
  const fileInputRef = React.useRef(null);

  useEffect(() => {
    fetchSupports();
  }, [selectedCategory]);

  const fetchSupports = async () => {
    try {
      setLoading(true);
      const params = selectedCategory !== 'all' ? `?category=${selectedCategory}` : '';
      const response = await api.get(`/supports/${params}`);
      setSupports(response.data || []);
    } catch (error) {
      console.error('Error fetching supports:', error);
      // Use empty array if no supports found
      setSupports([]);
    } finally {
      setLoading(false);
    }
  };

  const categories = [
    { id: 'all', label: 'Tous', icon: FolderOpen, count: supports.length },
    { id: 'logos', label: 'Logos', icon: Image, count: supports.filter(s => s.category === 'logos').length },
    { id: 'charte', label: 'Charte graphique', icon: Palette, count: supports.filter(s => s.category === 'charte').length },
    { id: 'visuels', label: 'Visuels', icon: Image, count: supports.filter(s => s.category === 'visuels').length },
    { id: 'videos', label: 'Vidéos', icon: Video, count: supports.filter(s => s.category === 'videos').length },
  ];

  const filteredSupports = supports.filter(s => {
    const matchesSearch = s.name?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesSearch;
  });

  const handleUpload = async (e) => {
    e.preventDefault();
    
    if (!newSupport.file) {
      toast.error('Veuillez sélectionner un fichier');
      return;
    }
    
    if (!newSupport.name.trim()) {
      toast.error('Veuillez donner un nom au fichier');
      return;
    }
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', newSupport.file);
      formData.append('name', newSupport.name);
      formData.append('category', newSupport.category);
      formData.append('description', newSupport.description || '');
      formData.append('shared_with', JSON.stringify([]));
      
      await api.post('/supports/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('Fichier téléversé avec succès !');
      setShowUploadModal(false);
      setNewSupport({ name: '', category: 'logos', description: '', file: null });
      fetchSupports();
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors du téléversement');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce fichier ?')) return;
    
    try {
      await api.delete(`/supports/${id}`);
      setSupports(supports.filter(s => s.id !== id));
      toast.success('Fichier supprimé');
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };
  
  const handleDownload = (support) => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
    window.open(`${backendUrl}${support.url}`, '_blank');
  };
  
  // Get all image supports for navigation
  const imageSupports = filteredSupports.filter(s => s.file_type === 'image');
  
  const handlePreview = (support) => {
    if (support.file_type === 'image') {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
      const imageIndex = imageSupports.findIndex(s => s.id === support.id);
      setSelectedImage({
        url: `${backendUrl}${support.url}`,
        name: support.name
      });
      setSelectedImageIndex(imageIndex >= 0 ? imageIndex : 0);
      setImageViewerOpen(true);
    } else {
      // For non-images, open in new tab
      handleDownload(support);
    }
  };
  
  const handleImageNavigate = (direction) => {
    const newIndex = direction === 'prev' 
      ? (selectedImageIndex - 1 + imageSupports.length) % imageSupports.length
      : (selectedImageIndex + 1) % imageSupports.length;
    
    const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
    const newImage = imageSupports[newIndex];
    setSelectedImage({
      url: `${backendUrl}${newImage.url}`,
      name: newImage.name
    });
    setSelectedImageIndex(newIndex);
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'image': return <Image className="w-8 h-8" />;
      case 'video': return <Video className="w-8 h-8" />;
      case 'document': return <FileText className="w-8 h-8" />;
      default: return <FolderOpen className="w-8 h-8" />;
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'image': return 'bg-blue-100 text-blue-600';
      case 'video': return 'bg-purple-100 text-purple-600';
      case 'document': return 'bg-orange-100 text-orange-600';
      default: return 'bg-gray-100 text-gray-600';
    }
  };

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <Sidebar
        collapsed={collapsed}
        setCollapsed={setCollapsed}
        mobileOpen={mobileOpen}
        setMobileOpen={setMobileOpen}
      />
      <Header collapsed={collapsed} setMobileOpen={setMobileOpen} />

      <main className={cn(
        "transition-all duration-300 p-6 md:p-8 pt-24",
        collapsed ? "md:ml-20" : "md:ml-64"
      )}>
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold" style={{ color: theme.colors?.primary }}>
              Mes supports
            </h1>
            <p className="text-[#64748b]">
              Gérez vos logos, charte graphique, visuels et vidéos
            </p>
          </div>
          <Button
            onClick={() => setShowUploadModal(true)}
            className="rounded-full"
            style={{ backgroundColor: theme.colors?.accent }}
          >
            <Upload className="w-4 h-4 mr-2" />
            Téléverser
          </Button>
        </div>

        {/* Categories */}
        <div className="flex flex-wrap gap-2 mb-6">
          {categories.map((cat) => {
            const Icon = cat.icon;
            return (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.id)}
                className={cn(
                  "flex items-center gap-2 px-4 py-2 rounded-full transition-all",
                  selectedCategory === cat.id
                    ? "text-white"
                    : "bg-white border border-[#e2e8f0] text-[#64748b] hover:border-[#cbd5e1]"
                )}
                style={selectedCategory === cat.id ? { backgroundColor: theme.colors?.primary } : {}}
              >
                <Icon className="w-4 h-4" />
                <span className="font-medium">{cat.label}</span>
                <Badge variant="secondary" className="ml-1">{cat.count}</Badge>
              </button>
            );
          })}
        </div>

        {/* Search and View Toggle */}
        <Card className="card mb-6">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
              <div className="relative flex-1 w-full">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748b]" />
                <Input
                  placeholder="Rechercher un fichier..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="input-default pl-10"
                />
              </div>
              <div className="flex items-center gap-2 bg-[#f8fafc] rounded-lg p-1">
                <Button
                  variant={viewMode === 'grid' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('grid')}
                  className="rounded-lg"
                >
                  <Grid className="w-4 h-4" />
                </Button>
                <Button
                  variant={viewMode === 'list' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('list')}
                  className="rounded-lg"
                >
                  <List className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Files Grid/List */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="spinner" />
          </div>
        ) : filteredSupports.length === 0 ? (
          <Card className="card">
            <CardContent className="p-12 text-center">
              <FolderOpen className="w-16 h-16 mx-auto mb-4 text-[#64748b] opacity-50" />
              <h3 className="text-lg font-semibold mb-2" style={{ color: theme.colors?.primary }}>
                Aucun fichier trouvé
              </h3>
              <p className="text-[#64748b] mb-4">
                {searchQuery ? 'Essayez une autre recherche' : 'Téléversez votre premier fichier'}
              </p>
              <Button
                onClick={() => setShowUploadModal(true)}
                className="rounded-full"
                style={{ backgroundColor: theme.colors?.accent }}
              >
                <Upload className="w-4 h-4 mr-2" />
                Téléverser un fichier
              </Button>
            </CardContent>
          </Card>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filteredSupports.map((support) => (
              <Card key={support.id} className="card card-hover group">
                <CardContent className="p-4">
                  {/* Preview - Show actual image or icon - clickable for images */}
                  <div 
                    className={cn(
                      "w-full h-32 rounded-xl flex items-center justify-center mb-4 overflow-hidden",
                      !support.url?.match(/\.(jpg|jpeg|png|gif|webp|svg)$/i) && getTypeColor(support.file_type),
                      support.file_type === 'image' && "cursor-pointer hover:opacity-90 transition-opacity"
                    )}
                    onClick={() => support.file_type === 'image' && handlePreview(support)}
                  >
                    {support.file_type === 'image' && support.url ? (
                      <img 
                        src={`${process.env.REACT_APP_BACKEND_URL || window.location.origin}${support.url}`}
                        alt={support.name}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <div className={cn(
                      "w-full h-full flex items-center justify-center",
                      support.file_type === 'image' && support.url ? "hidden" : ""
                    )}>
                      {getTypeIcon(support.file_type)}
                    </div>
                  </div>
                  
                  {/* Info */}
                  <div className="flex items-start justify-between">
                    <div className="min-w-0 flex-1">
                      <h3 className="font-medium truncate" style={{ color: theme.colors?.primary }}>
                        {support.name}
                      </h3>
                      <p className="text-xs text-[#64748b]">
                        {support.format} • {support.size_formatted || support.size}
                      </p>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="opacity-0 group-hover:opacity-100 rounded-full">
                          <MoreHorizontal className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handlePreview(support)}>
                          <Eye className="w-4 h-4 mr-2" />
                          Aperçu
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleDownload(support)}>
                          <Download className="w-4 h-4 mr-2" />
                          Télécharger
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleDelete(support.id)} className="text-red-600">
                          <Trash2 className="w-4 h-4 mr-2" />
                          Supprimer
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                  
                  <div className="flex items-center justify-between mt-3 pt-3 border-t border-[#e2e8f0]">
                    <span className="text-xs text-[#94a3b8]">{support.created_at ? new Date(support.created_at).toLocaleDateString('fr-FR') : '-'}</span>
                    <Badge variant="outline" className="text-xs">{support.uploaded_by || 'Moi'}</Badge>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="card">
            <CardContent className="p-0">
              <table className="w-full">
                <thead className="bg-[#f8fafc] border-b">
                  <tr>
                    <th className="text-left p-4 text-sm font-medium text-[#64748b]">Nom</th>
                    <th className="text-left p-4 text-sm font-medium text-[#64748b]">Catégorie</th>
                    <th className="text-left p-4 text-sm font-medium text-[#64748b]">Format</th>
                    <th className="text-left p-4 text-sm font-medium text-[#64748b]">Taille</th>
                    <th className="text-left p-4 text-sm font-medium text-[#64748b]">Date</th>
                    <th className="text-right p-4 text-sm font-medium text-[#64748b]">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSupports.map((support) => (
                    <tr key={support.id} className="border-b hover:bg-[#f8fafc]">
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          <div className={cn("w-10 h-10 rounded-lg flex items-center justify-center", getTypeColor(support.file_type))}>
                            {React.cloneElement(getTypeIcon(support.file_type), { className: 'w-5 h-5' })}
                          </div>
                          <span className="font-medium" style={{ color: theme.colors?.primary }}>{support.name}</span>
                        </div>
                      </td>
                      <td className="p-4 text-[#64748b]">{categories.find(c => c.id === support.category)?.label || support.category}</td>
                      <td className="p-4"><Badge variant="outline">{support.format}</Badge></td>
                      <td className="p-4 text-[#64748b]">{support.size_formatted || support.size}</td>
                      <td className="p-4 text-[#64748b]">{support.created_at ? new Date(support.created_at).toLocaleDateString('fr-FR') : '-'}</td>
                      <td className="p-4 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="icon" className="rounded-full" onClick={() => handlePreview(support)}><Eye className="w-4 h-4" /></Button>
                          <Button variant="ghost" size="icon" className="rounded-full" onClick={() => handleDownload(support)}><Download className="w-4 h-4" /></Button>
                          <Button variant="ghost" size="icon" className="rounded-full text-red-600" onClick={() => handleDelete(support.id)}><Trash2 className="w-4 h-4" /></Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        )}

        {/* Upload Modal */}
        <Dialog open={showUploadModal} onOpenChange={setShowUploadModal}>
          <DialogContent aria-describedby="upload-description">
            <DialogHeader>
              <DialogTitle style={{ color: theme.colors?.primary }}>Téléverser un fichier</DialogTitle>
            </DialogHeader>
            <p id="upload-description" className="sr-only">Formulaire pour téléverser un nouveau fichier</p>
            <form onSubmit={handleUpload} className="space-y-4">
              <div className="space-y-2">
                <Label>Nom du fichier</Label>
                <Input
                  value={newSupport.name}
                  onChange={(e) => setNewSupport({ ...newSupport, name: e.target.value })}
                  placeholder="Ex: Logo principal"
                  className="input-default"
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label>Catégorie</Label>
                <select
                  value={newSupport.category}
                  onChange={(e) => setNewSupport({ ...newSupport, category: e.target.value })}
                  className="w-full px-3 py-2 border rounded-xl"
                >
                  <option value="logos">Logos</option>
                  <option value="charte">Charte graphique</option>
                  <option value="visuels">Visuels</option>
                  <option value="videos">Vidéos</option>
                </select>
              </div>
              
              <div className="space-y-2">
                <Label>Fichier</Label>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      setNewSupport({ 
                        ...newSupport, 
                        file,
                        name: newSupport.name || file.name.replace(/\.[^/.]+$/, '')
                      });
                    }
                  }}
                  accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.ai,.psd,.eps,.svg"
                  className="hidden"
                />
                <div 
                  onClick={() => fileInputRef.current?.click()}
                  className="border-2 border-dashed border-[#e2e8f0] rounded-xl p-8 text-center hover:border-[#cbd5e1] transition-colors cursor-pointer"
                >
                  {newSupport.file ? (
                    <div className="space-y-2">
                      <div className="w-12 h-12 mx-auto rounded-xl bg-emerald-100 flex items-center justify-center">
                        <FileText className="w-6 h-6 text-emerald-600" />
                      </div>
                      <p className="font-medium text-[#334155]">{newSupport.file.name}</p>
                      <p className="text-xs text-[#64748b]">
                        {(newSupport.file.size / (1024 * 1024)).toFixed(2)} MB
                      </p>
                      <Button 
                        type="button" 
                        variant="outline" 
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          setNewSupport({...newSupport, file: null});
                        }}
                      >
                        Changer de fichier
                      </Button>
                    </div>
                  ) : (
                    <>
                      <Upload className="w-10 h-10 mx-auto mb-2 text-[#64748b]" />
                      <p className="text-sm text-[#64748b]">Glissez-déposez ou cliquez pour sélectionner</p>
                      <p className="text-xs text-[#94a3b8] mt-1">PNG, JPG, PDF, MP4, etc. (max 50MB)</p>
                    </>
                  )}
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Description (optionnel)</Label>
                <Textarea
                  value={newSupport.description}
                  onChange={(e) => setNewSupport({ ...newSupport, description: e.target.value })}
                  placeholder="Description du fichier..."
                  className="input-default"
                />
              </div>
              
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowUploadModal(false)} className="rounded-full" disabled={uploading}>
                  Annuler
                </Button>
                <Button type="submit" className="rounded-full" style={{ backgroundColor: theme.colors?.accent }} disabled={uploading || !newSupport.file}>
                  {uploading ? (
                    <>
                      <div className="w-4 h-4 mr-2 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Envoi en cours...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      Téléverser
                    </>
                  )}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
        
        {/* Image Viewer Modal */}
        <ImageViewer
          isOpen={imageViewerOpen}
          onClose={() => setImageViewerOpen(false)}
          imageUrl={selectedImage?.url}
          imageName={selectedImage?.name}
          allImages={imageSupports}
          currentIndex={selectedImageIndex}
          onNavigate={handleImageNavigate}
        />
      </main>
    </div>
  );
};

export default SupportsPage;
